import { useMemo, useState } from 'react';
import type { SetElement } from 'react-use-rect';
import { useRect } from 'react-use-rect';

const BREAKPOINT_LAG = 30;

type Breakpoints<T extends string | symbol> = Record<T, number>;
type BreakpointsArray<T> = { key: T; value: number }[];

export function useStableBreakpoints<
  T extends string | symbol,
  K extends string | symbol
>(rawBreakpoints: Breakpoints<T>, beyondKey: K): [SetElement, T | K] {
  const [currentBreakpointIndex, setCurrentBreakpointIndex] = useState(0);

  const breakpoints = useMemo(
    () => toBreakpointsArray(rawBreakpoints),
    [rawBreakpoints]
  );

  const [measureRef] = useRect(({ width }) => {
    if (width == null) {
      return;
    }

    const idealIndex = findBreakpointIndex(width, breakpoints);

    // цель сужается - меняем брейкпоинт сразу
    if (idealIndex < currentBreakpointIndex) {
      setCurrentBreakpointIndex(idealIndex);

      return;
    }

    // цель расширяется - меняем брейкпоинт с применением лага для обеспечения стабильности
    if (
      idealIndex > currentBreakpointIndex &&
      width >= breakpoints[currentBreakpointIndex].value + BREAKPOINT_LAG
    ) {
      setCurrentBreakpointIndex(idealIndex);
    }
  });

  const isBeyond = currentBreakpointIndex >= breakpoints.length;
  const key = isBeyond ? beyondKey : breakpoints[currentBreakpointIndex].key;

  return [measureRef, key] as const;
}

function toBreakpointsArray<T extends string | symbol>(
  breakpoints: Breakpoints<T>
): BreakpointsArray<T> {
  return Object.keys(breakpoints)
    .map((key) => ({
      key: key as T,
      value: breakpoints[key as T]
    }))
    .sort((a, b) => a.value - b.value);
}

function findBreakpointIndex<T extends string | symbol>(
  width: number,
  breakpoints: BreakpointsArray<T>
): number {
  for (let i = 0; i < breakpoints.length; i++) {
    if (width < breakpoints[i].value) {
      return i;
    }
  }

  return breakpoints.length;
}
